import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { AppointmentModel, ChangeSet, EditingState, ValidResourceInstance, ViewState } from '@devexpress/dx-react-scheduler';
import { Scheduler, WeekView, Appointments, Toolbar, DragDropProvider, EditRecurrenceMenu } from '@devexpress/dx-react-scheduler-material-ui';
import GlobalContext from '../../context/GlobalContext';
import LinearProgress from '@mui/material/LinearProgress';
import { colors } from '../../theme/shared-theme';
import { Grid, ThemeProvider } from '@mui/material';
import { scheduleCalendarTheme } from '../../theme';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Icon } from '..';
import { CONSTANTS, calculateDailyTotals } from '../../utils';
import { CustomDialog } from '../dialog/customDialog/customDialog';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import { RootState } from '../../index';
import { DailyTotalsRow } from './DailyTotalsRow';
import { logger } from '../../utils/logger';

interface Calendarprops {
  showWeekends?: boolean;
  tasks?: AppointmentModel[];
  loading?: boolean;
  weekTypeSelected?: boolean;
}
const useStyles = makeStyles({
  scheduler: {
    '& .Cell-dayOfMonth': {
      display: 'none',
    },
  },
});

export const Calendar = React.memo((props: Calendarprops) => {
  const { showWeekends = false, tasks = [], loading, weekTypeSelected } = props;
  const gContext = useContext(GlobalContext);
  const listUsers = useSelector((state: RootState) => state.users.users?.items);
  const [currentDate, setCurrentDate] = useState(gContext?.calendarWeek ? gContext.calendarWeek.toDate() : new Date());
  const [editDialog, setEditDialog] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState<AppointmentModel | null>(null);
  const [showDeleteDialog, setShowChangeDialog] = useState(false);
  const { t } = useTranslation();
  const classes = useStyles();
  useEffect(() => {
    if (!gContext?.calendarWeek) {
      return;
    }
    var new_date = moment(gContext.calendarWeek).add(7, 'days');
    if(gContext?.weekTypeSelected ){
      logger.debug('Calendar', 'Week type selected, setting date to next week', { weekTypeSelected: gContext?.weekTypeSelected });
      setCurrentDate(new_date.toDate());
    }else{
      logger.debug('Calendar', 'Week type not selected, setting date to current week', { calendarWeek: gContext.calendarWeek.toISOString() });
      setCurrentDate(gContext.calendarWeek.toDate());
    }
  }, [gContext?.calendarWeek,gContext?.weekTypeSelected]);

  useEffect(() => {
    logger.debug('Calendar', 'Tasks changed', { count: props.tasks?.length });
  }, [props.tasks])

  const handleOpenEdit = useCallback((data: AppointmentModel | null) => {
    logger.debug('Calendar', 'Opening edit dialog', { taskId: data?.id });
    setTaskToEdit(data);
    setEditDialog(true);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditDialog(false);
    setTaskToEdit(null);
  }, []);

  const handelChangeDialog = useCallback((value: boolean) => {
    if (!value) {
      handleCloseEdit();
    } else {
      handleOpenEdit(taskToEdit);
    }
  }, [handleCloseEdit, handleOpenEdit, taskToEdit]);

  const onDeleteTask = useCallback(() => {
    setShowChangeDialog(!showDeleteDialog);
  }, [showDeleteDialog]);

  const getAppointmentBackgroundColor = (data: AppointmentModel): string => {
    if (data.status === CONSTANTS.STATUS_VALIDATED) {
      return colors.SECONDARY_GREEN;
    }
    if (data.status === CONSTANTS.STATUS_DONE) {
      return colors.MEDIUM_GREY;
    }
    
    if (data.isJobSchedule) {
      return data.collaborators && data.collaborators[0] && data.collaborators[0].distinctColor
        ? data.collaborators[0].distinctColor
        : colors.MAIN_BLUE;
    } else {
      return listUsers && data.collaborators && data.collaborators[0]
        ? (listUsers.find(item => item.id === data.collaborators[0].id)?.distinctColor || colors.DARK_GREY)
        : colors.DARK_GREY;
    }
  };

  const Appointment = (appointmentProps: {
    children?: React.ReactNode;
    data: AppointmentModel;
    draggable?: boolean;
    isShaded?: boolean;
    resources?: Array<ValidResourceInstance>;
    [key: string]: unknown;
  }) => (
    <Appointments.Appointment
    {...appointmentProps}
    style={{
      opacity: weekTypeSelected ? (appointmentProps.data.status === 1 ? 0.3 : 1) : 1,
      backgroundColor: getAppointmentBackgroundColor(appointmentProps.data),
      color: colors.WHITE,
    }}
    onClick={() => handleOpenEdit(appointmentProps.data)}
  >
      <Grid className="appointment-content">
        <Grid container className="black-text">
          { (appointmentProps.data.isIntervention) ? 'Intervention' : 'Prestation' }
        </Grid>
        <Grid container className="title">
          {appointmentProps.data.types && appointmentProps.data.types.length > 0 
            ? appointmentProps.data.types[0].type 
            : 'N/A'}
        </Grid>
        {(!weekTypeSelected) &&
        <Grid container className="date">
          {appointmentProps.data.startDate && appointmentProps.data.endDate
            ? `${moment(appointmentProps.data.startDate).format('HH:mm')} ${t('common.to')} ${moment(appointmentProps.data.endDate).format('HH:mm')}`
            : ''}
        </Grid>
        }
        <Grid container className="ref">
          {appointmentProps.data.refPat || ''}
        </Grid>
        {weekTypeSelected ? (
          <></>
        ) : appointmentProps.data.status === CONSTANTS.STATUS_IN_PROGRESS ? (
          <Icon name={'planningClock'} prefix="domanys" />
        ) : (
          <></>
        )}
        {weekTypeSelected ? <></> : appointmentProps.data.status === CONSTANTS.STATUS_VALIDATED ? <Icon name={'planningDone'} prefix="domanys" /> : <></>}
        {weekTypeSelected ? <></> : appointmentProps.data.status === CONSTANTS.STATUS_DONE ? <Icon name={'planningDone'} prefix="domanys" /> : <></>}
      </Grid>
    </Appointments.Appointment>
  );

  const ToolbarWithLoading = ({ children, ...restProps }: { children?: React.ReactNode }) => (
    <>
      <Toolbar.Root {...restProps}>{children}</Toolbar.Root>
      <LinearProgress />
    </>
  );

  const commitChanges = useCallback((changeProps: ChangeSet) => {
    logger.debug('Calendar', 'Task dropped', { changed: !!changeProps.changed, added: !!changeProps.added, deleted: !!changeProps.deleted });
    return tasks;
  }, [tasks]);

  const dailyTotals = useMemo(() => {
    return calculateDailyTotals(tasks, showWeekends, currentDate);
  }, [tasks, showWeekends, currentDate]);

  return (
    <>
      <ThemeProvider theme={scheduleCalendarTheme}>
        {weekTypeSelected ? (
          <div className={classes.scheduler}>
            <Scheduler data={tasks} locale={'fr-FR'} firstDayOfWeek={1}>
              <ViewState defaultCurrentDate={new Date()} currentDate={currentDate} />
              <Toolbar {...(loading ? { rootComponent: ToolbarWithLoading } : null)} />
              <EditingState onCommitChanges={commitChanges} />
              <WeekView
                cellDuration={60}
                excludedDays={!showWeekends ? [0, 6] : []}
                startDayHour={6}
                endDayHour={20}
              />
              <Appointments appointmentComponent={Appointment} />
              <EditRecurrenceMenu />
              <DragDropProvider />
            </Scheduler>
            <DailyTotalsRow
              dailyTotals={dailyTotals}
              showWeekends={showWeekends}
              currentWeek={currentDate}
            />
          </div>
        ) : (
          <>
            <Scheduler data={tasks} locale={'fr-FR'} firstDayOfWeek={1}>
              <ViewState defaultCurrentDate={new Date()} currentDate={currentDate} />
              <Toolbar {...(loading ? { rootComponent: ToolbarWithLoading } : null)} />
              <EditingState onCommitChanges={commitChanges} />
              <WeekView
                cellDuration={60}
                excludedDays={!showWeekends ? [0, 6] : []}
                startDayHour={6}
                endDayHour={20}
              />
              <Appointments appointmentComponent={Appointment} />
              <EditRecurrenceMenu />
              <DragDropProvider />
            </Scheduler>
            <DailyTotalsRow
              dailyTotals={dailyTotals}
              showWeekends={showWeekends}
              currentWeek={currentDate}
            />
          </>
        )}
      </ThemeProvider>
      <CustomDialog
        show={editDialog}
        loading={false}
        title={''}
        changeDialog={handelChangeDialog}
        showDeleteDialog={showDeleteDialog}
        onDelete={onDeleteTask}
        setShowChangeDialog={setShowChangeDialog}
        handleCloseEdit={handleCloseEdit}
        canDelete={true}
        showAction={taskToEdit?.status === CONSTANTS.STATUS_TO_DO}
        showStatus={taskToEdit?.status !== CONSTANTS.STATUS_TO_DO}
        weekTypeSelected={weekTypeSelected}
      >
        <Grid>{taskToEdit}</Grid>
      </CustomDialog>
    </>
  );
});
